import type { OralExamSubmissionEvaluation } from '@/common/api/models/oralExamSubmissionEvaluation';
import type { PerformanceLevelConfig } from '@/common/api/models/performanceLevelConfig';

export const ORAL_EXAM_DIMENSION_COLORS: Record<string, string> = {
  content: '#2563eb',
  depth: '#7c3aed',
  application: '#dc2626',
  communication: '#0891b2',
  professionalism: '#64748b',
};

const isRatedLevel = (level: string | undefined | null): level is string => (
  !!level && level !== 'n/a'
);

export const averageLevelFromLevels = (
  levels: Array<string | undefined | null>,
  levelWeights: Record<string, number>,
  performanceLevels: PerformanceLevelConfig[],
): string | null => {
  const ratedLevels = levels.filter(isRatedLevel);
  if (ratedLevels.length === 0) return null;

  const avgWeight = ratedLevels.reduce((sum, level) => sum + (levelWeights[level] ?? 0), 0) / ratedLevels.length;
  const candidates = performanceLevels.filter((level) => level.id !== 'n/a');
  if (candidates.length === 0) return null;

  return candidates.reduce((best, candidate) => {
    const diff = Math.abs((levelWeights[candidate.id] ?? 0) - avgWeight);
    const bestDiff = Math.abs((levelWeights[best.id] ?? 0) - avgWeight);
    return diff < bestDiff ? candidate : best;
  }).id;
};

export const averageDimLevel = (
  subEvaluations: OralExamSubmissionEvaluation['subEvaluations'],
  dimension: string,
  levelWeights: Record<string, number>,
  performanceLevels: PerformanceLevelConfig[],
): string | null => averageLevelFromLevels(
  subEvaluations.map((subEvaluation) => subEvaluation.dimGrades[dimension]?.level),
  levelWeights,
  performanceLevels,
);

export const averageQuestionLevel = (
  dimGrades: Record<string, { level?: string } | undefined>,
  levelWeights: Record<string, number>,
  performanceLevels: PerformanceLevelConfig[],
): string | null => averageLevelFromLevels(
  Object.values(dimGrades).map((grade) => grade?.level),
  levelWeights,
  performanceLevels,
);

export const chunkArray = <T>(items: T[], size: number): T[][] => {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
};

export const getOralExamDimensionColor = (
  dimension: string,
  dimensionColors: Record<string, string>,
): string => dimensionColors[dimension] || ORAL_EXAM_DIMENSION_COLORS[dimension] || '#666666';
